export enum ChecklistItemType {
  CAMPAIGN_SENT = 'CAMPAIGN_SENT',
  NEW_CLIENTS_RETURN = 'NEW_CLIENTS_RETURN',
  AUTOMATED_MARKETING_RETURN = 'AUTOMATED_MARKETING_RETURN',
  REFERRAL = 'REFERRAL',
  GIFT_WHEEL_REVIEWS = 'GIFT_WHEEL_REVIEWS',
  DIRECTORIES_SYNCED = 'DIRECTORIES_SYNCED',
  GOOGLE_POST = 'GOOGLE_POST',
}

export enum ChecklistGroup {
  LOYALTY = 'LOYALTY',
  BOOST = 'BOOST',
  REACH = 'REACH',
}

export type ChecklistItemProgress = {
  type: ChecklistItemType;
  group: ChecklistGroup;
  threshold: number;
  /** A count, or a percentage for the return items. */
  current: number;
  isDone: boolean;
  isManuallyValidated: boolean;
};

export type ProgressLevel = 'low' | 'fair' | 'good' | 'high';

export enum LaunchTaskType {
  GOOGLE_REVIEW_LINK = 'GOOGLE_REVIEW_LINK',
  A5_POSTER = 'A5_POSTER',
  SOCIAL_PROMO = 'SOCIAL_PROMO',
  TEAM_PITCH_TRAINING = 'TEAM_PITCH_TRAINING',
  NFC_TAG_TRAINING = 'NFC_TAG_TRAINING',
}

export type LaunchTaskProgress = {
  type: LaunchTaskType;
  /** Ticks itself from the store data; nobody can tick it by hand. */
  isAutomatic: boolean;
  isDone: boolean;
  checkedAt: string | null;
  isCheckedByLittleBill: boolean | null;
};

export type LaunchTasks = {
  storeId: string;
  tasks: LaunchTaskProgress[];
  doneCount: number;
  totalCount: number;
};

export type LaunchTaskSettings = {
  type: LaunchTaskType;
  isEnabled: boolean;
};

export type LaunchTaskOverride = {
  type: LaunchTaskType;
  isEnabled?: boolean | null;
};

export enum NewClientsGoalPhase {
  WAITING = 'WAITING',
  FIRST_MONTH = 'FIRST_MONTH',
  SECOND_MONTH = 'SECOND_MONTH',
  ONGOING = 'ONGOING',
}

export type NewClientsGoal = {
  phase: NewClientsGoalPhase;
  /** The first-month target, then the second-month one, kept after the launch. */
  target: number;
  /** New clients since the first one; keeps growing past the target. */
  current: number;
  /** End of the first or second month; null while waiting or ongoing. */
  phaseEnd: string | null;
  isReached: boolean;
};

export type HomeObjective = {
  storeId: string;
  title: string;
  /** The store's first client; null until there is one. */
  periodStart: string | null;
  firstMonthTarget: number;
  secondMonthTarget: number;
  goal: NewClientsGoal;
  items: ChecklistItemProgress[];
  doneCount: number;
  totalCount: number;
};

export type ChecklistItemSettings = {
  type: ChecklistItemType;
  isEnabled: boolean;
  threshold: number;
};

export type HomeObjectiveSettings = {
  title: string;
  firstMonthTarget: number;
  secondMonthTarget: number;
  items: ChecklistItemSettings[];
  launchTasks: LaunchTaskSettings[];
};

export type ChecklistItemOverride = {
  type: ChecklistItemType;
  isEnabled?: boolean | null;
  threshold?: number | null;
};

export type StoreHomeObjectiveOverride = {
  title?: string | null;
  firstMonthTarget?: number | null;
  secondMonthTarget?: number | null;
  items: ChecklistItemOverride[];
  launchTasks: LaunchTaskOverride[];
};

export type StoreHomeObjectiveAdmin = {
  storeId: string;
  common: HomeObjectiveSettings;
  override: StoreHomeObjectiveOverride;
  effective: HomeObjectiveSettings;
  manuallyValidated: ChecklistItemType[];
  progress: HomeObjective;
};

export enum HomeVideoSection {
  OFFERS_NEWS = 'OFFERS_NEWS',
  TRAINING = 'TRAINING',
  TESTIMONIALS = 'TESTIMONIALS',
}

export type HomeVideo = {
  id: string;
  youtubeUrl: string;
  youtubeId: string;
  section: HomeVideoSection;
  label: string;
  title: string;
  description: string;
  position: number;
  isPublished: boolean;
};

export type HomeVideoInput = Pick<
  HomeVideo,
  'youtubeUrl' | 'section' | 'label' | 'title' | 'description' | 'isPublished'
>;
